// =============================================================================
// Phase 4: Absolute Date Parser
// Parses absolute dates in various formats: ISO, US, EU, month names
// =============================================================================

import { getDaysInMonth, validateDate, validateTime } from "./utils/calendar";
import { MONTH_TO_NUMBER } from "./utils/constants";
import { parse12HourFormat, parse24HourFormat, parseHourOnly as parseHourOnlyTime } from "./timeParser";

export interface DateResult {
  isValid: boolean;
  year?: number;
  month?: number;
  day?: number;
  hour?: number;
  minute?: number;
  second?: number;
  millisecond?: number;
  timezone?: string;
  error?: string;
}

export interface ParseOptions {
  preferEU?: boolean;
}

/**
 * Convert 2-digit year to 4-digit year
 * Years 00-49 -> 2000-2049, 50-99 -> 1950-1999
 */
function expandTwoDigitYear(year: number): number {
  if (year >= 100) return year;
  return year < 50 ? 2000 + year : 1900 + year;
}

/**
 * Parse month name to number
 */
function parseMonthName(name: string): number | null {
  const lower = name.toLowerCase();
  return MONTH_TO_NUMBER[lower] ?? null;
}

/**
 * Parse ordinal day (1st, 2nd, 3rd, 4th, etc.)
 */
function parseOrdinalDay(input: string): number | null {
  const match = input.match(/^(\d+)(?:st|nd|rd|th)$/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Check if a token looks like time
 */
function looksLikeTime(token: string): boolean {
  // 24-hour format: HH:MM or HH:MM:SS
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(token)) return true;
  // 12-hour format: 2pm, 2:30pm
  if (/^\d{1,2}(:\d{2})?(am|pm)$/i.test(token)) return true;
  // Separate am/pm
  if (/^(am|pm)$/i.test(token)) return true;
  // Single/double digit that could be an hour (0-23)
  if (/^\d{1,2}$/.test(token)) {
    const num = parseInt(token, 10);
    if (num >= 0 && num <= 23) return true;
  }
  return false;
}

/**
 * Check if token looks like a year
 */
function looksLikeYear(token: string): boolean {
  // Must be all digits
  if (!/^\d+$/.test(token)) return false;
  const num = parseInt(token, 10);
  // 4-digit year (1000-9999)
  if (token.length === 4 && num >= 1000 && num <= 9999) return true;
  // 2-digit year - only if 24-99 (0-23 could be hours)
  // For DevOps context, "jan 15 14" means 14:00, not year 2014
  if (token.length === 2 && num >= 24 && num <= 99) return true;
  return false;
}

/**
 * Parse time from tokens array
 * Uses shared time parsing functions from timeParser.ts
 */
function parseTimeFromTokens(tokens: string[]): { hour: number; minute: number; second?: number } | null {
  if (tokens.length === 0) return null;

  // Check for "at" keyword and skip it
  let idx = 0;
  if (tokens[idx].toLowerCase() === "at") {
    idx++;
  }

  if (idx >= tokens.length) return null;

  // Try parsing time token(s)
  let timeStr = tokens[idx];

  // Check if next token is am/pm (separate)
  if (idx + 1 < tokens.length && /^(am|pm)$/i.test(tokens[idx + 1])) {
    timeStr = tokens[idx] + tokens[idx + 1];
  }

  // Try 12-hour format using shared parser
  const time12 = parse12HourFormat(timeStr);
  if (time12 && time12.isValid) {
    return { hour: time12.hour!, minute: time12.minute!, second: time12.second };
  }

  // Try 24-hour format using shared parser
  const time24 = parse24HourFormat(tokens[idx]);
  if (time24 && time24.isValid) {
    return { hour: time24.hour!, minute: time24.minute!, second: time24.second };
  }

  // Try hour only using shared parser
  const hourOnly = parseHourOnlyTime(tokens[idx]);
  if (hourOnly && hourOnly.isValid) {
    return { hour: hourOnly.hour!, minute: hourOnly.minute ?? 0 };
  }

  return null;
}

/**
 * Main parser function
 */
export function parseAbsoluteDate(input: string, options: ParseOptions = {}): DateResult {
  const trimmed = input.trim();

  // Handle empty input
  if (!trimmed) {
    return { isValid: false };
  }

  // Try Unix timestamp first (must check before other numeric formats)
  const unixResult = parseUnixTimestamp(trimmed);
  if (unixResult) return unixResult;

  // Try ISO format first
  const isoResult = parseISOFormat(trimmed);
  if (isoResult) return isoResult;

  // Try compact ISO format (YYYYMMDD)
  const compactResult = parseCompactISO(trimmed);
  if (compactResult) return compactResult;

  // Try numeric formats (US/EU with slashes, dashes, dots)
  const numericResult = parseNumericFormat(trimmed, options);
  if (numericResult) return numericResult;

  // Try month name formats
  const monthNameResult = parseMonthNameFormat(trimmed);
  if (monthNameResult) return monthNameResult;

  // Invalid input
  throw new Error(`Invalid date format: ${input}`);
}

/**
 * Parse Unix timestamp (seconds or milliseconds since epoch)
 * - 9-10 digits: seconds (e.g., 1735689600 = Jan 1, 2025 00:00:00 UTC)
 * - 13 digits: milliseconds (e.g., 1735689600000)
 */
function parseUnixTimestamp(input: string): DateResult | null {
  // Only accept pure numeric strings (9-13 digits)
  // 9 digits covers timestamps from ~2001 (1000000000) back to ~1970
  if (!/^\d{9,13}$/.test(input)) return null;

  const num = parseInt(input, 10);

  // Determine if seconds or milliseconds based on magnitude
  // Reasonable range: 1970 to ~2286 for seconds, same for milliseconds
  let date: Date;
  let milliseconds = 0;

  if (input.length === 13 || num > 9999999999) {
    // Milliseconds timestamp
    date = new Date(num);
    milliseconds = num % 1000;
  } else {
    // Seconds timestamp
    date = new Date(num * 1000);
  }

  // Validate the resulting date is reasonable (1970-2100)
  const year = date.getUTCFullYear();
  if (year < 1970 || year > 2100) return null;

  return {
    isValid: true,
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
    millisecond: milliseconds,
    timezone: "UTC",
  };
}

/**
 * Parse ISO 8601 format
 */
function parseISOFormat(input: string): DateResult | null {
  // Full ISO: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS or with space separator
  const isoRegex = /^(\d{4})-(\d{2})-(\d{2})(?:[\sT](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{3,6}))?)?([Zz]|[+-]\d{2}:\d{2})?$/;
  const match = input.match(isoRegex);

  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  validateDate(year, month, day);

  const result: DateResult = {
    isValid: true,
    year,
    month,
    day,
  };

  // Parse time if present
  if (match[4] !== undefined) {
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = match[6] ? parseInt(match[6], 10) : 0;

    validateTime(hour, minute, second);

    result.hour = hour;
    result.minute = minute;
    result.second = second;
  }

  // Parse milliseconds
  if (match[7]) {
    const ms = match[7].slice(0, 3).padEnd(3, "0");
    result.millisecond = parseInt(ms, 10);
  }

  // Parse timezone
  if (match[8]) {
    result.timezone = match[8].toUpperCase() === "Z" ? "UTC" : match[8];
  }

  return result;
}

/**
 * Parse compact ISO format (YYYYMMDD)
 */
function parseCompactISO(input: string): DateResult | null {
  if (!/^\d{8}$/.test(input)) return null;

  const year = parseInt(input.slice(0, 4), 10);
  const month = parseInt(input.slice(4, 6), 10);
  const day = parseInt(input.slice(6, 8), 10);

  validateDate(year, month, day);

  return {
    isValid: true,
    year,
    month,
    day,
  };
}

/**
 * Parse numeric format (US/EU with separators)
 */
function parseNumericFormat(input: string, options: ParseOptions): DateResult | null {
  // Match: DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, DD.MM.YYYY, etc.
  const match = input.match(/^(\d{1,2})([\/\-\.])(\d{1,2})\2(\d{2,4})$/);

  if (!match) return null;

  const first = parseInt(match[1], 10);
  const second = parseInt(match[3], 10);
  let year = parseInt(match[4], 10);

  // Expand 2-digit year
  year = expandTwoDigitYear(year);

  let month: number;
  let day: number;

  // Determine if EU or US format
  // Auto-detect: if first number > 12, must be day (EU format)
  if (first > 12) {
    // Must be EU format (day first)
    day = first;
    month = second;
  } else if (second > 12) {
    // Must be US format (month first, day second would be > 12)
    month = first;
    day = second;
  } else {
    // Ambiguous - use preference
    if (options.preferEU) {
      day = first;
      month = second;
    } else {
      month = first;
      day = second;
    }
  }

  validateDate(year, month, day);

  return {
    isValid: true,
    year,
    month,
    day,
  };
}

/**
 * Parse month name format
 */
function parseMonthNameFormat(input: string): DateResult | null {
  const lower = input.toLowerCase();

  // Remove "the" and "of" for patterns like "the 15th of january"
  const normalized = lower
    .replace(/^the\s+/, "")
    .replace(/\s+of\s+/g, " ");

  // Tokenize
  const tokens = normalized.split(/[\s,]+/).filter(t => t.length > 0);

  if (tokens.length < 2) return null;

  // Try different patterns
  let month: number | null = null;
  let day: number | null = null;
  let year: number | undefined;
  let timeTokens: string[] = [];

  // Pattern 1: Month Day [Year] [Time] or Month Year
  // e.g., "jan 15", "january 15 2026", "jan 15 2026 14:30", "jan 15 14:30", "jan 2026"
  month = parseMonthName(tokens[0]);
  if (month !== null) {
    const token1 = tokens[1];

    // Check if token1 is a 4-digit year (Month Year pattern)
    // e.g., "jan 2026" means January 2026 (day defaults to context)
    if (/^\d{4}$/.test(token1)) {
      const yearNum = parseInt(token1, 10);
      if (yearNum >= 1000 && yearNum <= 9999) {
        // Month Year pattern - return without day
        return {
          isValid: true,
          month,
          year: yearNum,
          // No day - caller should fill in appropriate day based on context
        };
      }
    }

    // Parse day (with or without ordinal)
    const dayStr = token1;
    day = parseOrdinalDay(dayStr) ?? parseInt(dayStr, 10);

    if (isNaN(day)) {
      throw new Error(`Invalid day: ${dayStr}`);
    }

    // Check for year or time in position 2
    if (tokens.length >= 3) {
      const token2 = tokens[2];

      // Check if this looks like a year (4 digits or 2 digits that aren't time)
      if (looksLikeYear(token2)) {
        year = expandTwoDigitYear(parseInt(token2, 10));
        timeTokens = tokens.slice(3);
      } else {
        // Not a year, treat rest as time
        timeTokens = tokens.slice(2);
      }
    }

    // Validate
    validateDate(year, month, day);

    const result: DateResult = { isValid: true, month, day };
    if (year !== undefined) result.year = year;

    // Parse time if present
    if (timeTokens.length > 0) {
      const time = parseTimeFromTokens(timeTokens);
      if (time) {
        result.hour = time.hour;
        result.minute = time.minute;
        if (time.second !== undefined) result.second = time.second;
      }
    }

    return result;
  }

  // Pattern 2: Day Month [Year] [Time]
  // e.g., "15 jan", "15 january 2026", "15th jan 2026 14:30", "15 jan 14:30"
  const dayStr = tokens[0];
  day = parseOrdinalDay(dayStr) ?? parseInt(dayStr, 10);

  if (!isNaN(day)) {
    month = parseMonthName(tokens[1]);
    if (month !== null) {
      // Check for year or time in position 2
      if (tokens.length >= 3) {
        const token2 = tokens[2];
        const possibleYear = parseInt(token2, 10);

        // Check if this looks like a year (4 digits or 2 digits that aren't time)
        if (!isNaN(possibleYear) && !looksLikeTime(token2) && (possibleYear >= 1000 || (possibleYear < 100 && possibleYear >= 0))) {
          year = expandTwoDigitYear(possibleYear);
          timeTokens = tokens.slice(3);
        } else {
          // Not a year, treat rest as time
          timeTokens = tokens.slice(2);
        }
      }

      // Validate
      validateDate(year, month, day);

      const result: DateResult = { isValid: true, month, day };
      if (year !== undefined) result.year = year;

      // Parse time if present
      if (timeTokens.length > 0) {
        const time = parseTimeFromTokens(timeTokens);
        if (time) {
          result.hour = time.hour;
          result.minute = time.minute;
          if (time.second !== undefined) result.second = time.second;
        }
      }

      return result;
    }
  }

  // No valid pattern found
  throw new Error(`Invalid date format: ${input}`);
}
